import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

// ===========================================================================
// CORREGIR EL MES DE UN LOTE YA SUBIDO, Y BORRAR LO QUE NO DEBIÓ ENTRAR.
//
// El error más común de la carga no es el archivo: es el mes. Se sueltan
// treinta expedientes seguidos y el mes declarado se quedó en el anterior. Sin
// esto, la única salida era borrarlos del todo y volver a subirlos — pagando
// otra vez la lectura del modelo y perdiendo las notas y los recortes.
//
// LO CONCILIADO NO SE MUEVE NI SE BORRA. Un cruce ata la boleta a un abono que
// vive en el mes de su extracto: llevarse la boleta dejaría el cruce a caballo
// entre dos meses, y borrarla lo dejaría apuntando a algo que ya no existe. Se
// avisa aquí y el servidor lo vuelve a comprobar; un botón gris es una
// cortesía, no una comprobación.
// ===========================================================================

type Mes = { id: string; nombre: string };

/**
 * La respuesta del lote, escrita a mano.
 *
 * ts-rest 3.x no propaga los tipos de Zod 4, así que el `body` llega como
 * `any`. Se declara aquí lo que de verdad se lee, que es menos de lo que el
 * contrato devuelve.
 */
type Respuesta = {
  status: number;
  body: {
    dice?: string;
    rechazados?: Array<{ nombre: string; motivo: string }>;
  };
};

type Abierto = { status: number; body: { id?: string; nombre?: string } };

function Marco({
  titulo,
  children,
  acciones,
  alCerrar,
}: {
  titulo: string;
  children: React.ReactNode;
  acciones: React.ReactNode;
  alCerrar: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg border bg-background p-5 shadow-lg">
        <h2 className="font-semibold text-[15px]">{titulo}</h2>
        {children}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={alCerrar}>
            Cancelar
          </Button>
          {acciones}
        </div>
      </div>
    </div>
  );
}

/**
 * Lo que el servidor no dejó hacer, dicho por su nombre.
 *
 * Un contador de rechazados no sirve para nada: hay que saber CUÁLES, porque lo
 * siguiente es ir a soltarles el cruce.
 */
function avisar(
  rechazados: Array<{ nombre: string; motivo: string }>,
  dice: string,
) {
  if (rechazados.length === 0) {
    toast.success(dice);
    return;
  }
  toast.warning(dice, {
    description: rechazados
      .slice(0, 5)
      .map((r) => r.nombre.replace(/^.*\//, ""))
      .join(", "),
    duration: 8000,
  });
}

/** Llevar los expedientes elegidos a otro mes. */
export function MoverExpedientes({
  archivoIds,
  meses,
  mesActual,
  alTerminar,
  alCerrar,
}: {
  archivoIds: string[];
  meses: Mes[];
  /** El mes que se está mirando: no tiene sentido mover ahí. */
  mesActual: string;
  alTerminar: () => void;
  alCerrar: () => void;
}) {
  const qc = useQueryClient();
  const mover = api.moverExpedientes.useMutation();
  const abrirMes = api.crearPeriodo.useMutation();
  const otros = meses.filter((m) => m.nombre !== mesActual);
  const [periodoId, setPeriodoId] = useState(otros[0]?.id ?? "");
  const [mesNuevo, setMesNuevo] = useState("");

  const yaEsta = meses.some((m) => m.nombre === mesNuevo);
  const mesValido = /^\d{4}-(0[1-9]|1[0-2])$/.test(mesNuevo);

  const alAbrirMes = () => {
    abrirMes.mutate(
      { body: { nombre: mesNuevo } },
      {
        onSuccess: (r: Abierto) => {
          if (r.status !== 200 || !r.body.id) {
            toast.error("No se pudo abrir el mes");
            return;
          }
          // Se deja elegido: quien acaba de abrirlo es porque quiere mover ahí.
          setPeriodoId(r.body.id);
          setMesNuevo("");
          void qc.invalidateQueries();
          toast.success(`${r.body.nombre} abierto`);
        },
        onError: () => toast.error("No se pudo abrir el mes"),
      },
    );
  };

  const alMover = () => {
    mover.mutate(
      { body: { archivoIds, periodoId } },
      {
        onSuccess: (r: Respuesta) => {
          if (r.status !== 200) {
            toast.error("No se pudieron mover");
            return;
          }
          avisar(r.body.rechazados ?? [], r.body.dice ?? "Listo");
          void qc.invalidateQueries();
          alTerminar();
          alCerrar();
        },
        onError: () => toast.error("No se pudieron mover"),
      },
    );
  };

  return (
    <Marco
      titulo={`Mover ${archivoIds.length} ${archivoIds.length === 1 ? "expediente" : "expedientes"} a otro mes`}
      alCerrar={alCerrar}
      acciones={
        <Button
          size="sm"
          disabled={!periodoId || mover.isPending}
          onClick={alMover}
        >
          {mover.isPending ? "Moviendo…" : "Mover"}
        </Button>
      }
    >
      <p className="mt-1 text-[12.5px] text-muted-foreground">
        Se lleva el expediente entero: la boleta, la ficha y los vouchers. Nada
        se vuelve a leer, así que las notas y los recortes se quedan como están.
      </p>

      <div className="mt-4">
        <span className="font-medium text-[12.5px]">Mes de destino</span>
        <select
          aria-label="Mes de destino"
          className="mt-1 w-full rounded border bg-background px-2 py-1.5 text-[12.5px]"
          value={periodoId}
          onChange={(e) => setPeriodoId(e.target.value)}
        >
          {otros.length === 0 ? (
            <option value="">— no hay otro mes abierto —</option>
          ) : null}
          {otros.map((m) => (
            <option key={m.id} value={m.id}>
              {m.nombre}
            </option>
          ))}
        </select>

        {/* El mes al que hay que mover puede no existir todavía: se subió todo
            a enero justamente porque febrero no estaba abierto. */}
        <div className="mt-1.5 flex items-center gap-2">
          <input
            value={mesNuevo}
            onChange={(e) => setMesNuevo(e.target.value)}
            placeholder="¿No está? AAAA-MM"
            className="w-32 rounded border bg-background px-2 py-1 text-[11.5px]"
          />
          <Button
            variant="outline"
            size="sm"
            disabled={!mesValido || yaEsta || abrirMes.isPending}
            onClick={alAbrirMes}
          >
            {abrirMes.isPending
              ? "Abriendo…"
              : yaEsta
                ? "Ese mes ya está"
                : "Abrir el mes"}
          </Button>
        </div>
      </div>
    </Marco>
  );
}

/** Borrar los expedientes elegidos, con todo lo que trajeron. */
export function BorrarExpedientes({
  archivoIds,
  alTerminar,
  alCerrar,
}: {
  archivoIds: string[];
  alTerminar: () => void;
  alCerrar: () => void;
}) {
  const qc = useQueryClient();
  const borrar = api.eliminarExpedientes.useMutation();

  const alBorrar = () => {
    borrar.mutate(
      { body: { archivoIds, confirmo: true } },
      {
        onSuccess: (r: Respuesta) => {
          if (r.status !== 200) {
            toast.error("No se pudieron borrar");
            return;
          }
          avisar(r.body.rechazados ?? [], r.body.dice ?? "Listo");
          void qc.invalidateQueries();
          alTerminar();
          alCerrar();
        },
        onError: () => toast.error("No se pudieron borrar"),
      },
    );
  };

  return (
    <Marco
      titulo={`Borrar ${archivoIds.length} ${archivoIds.length === 1 ? "expediente" : "expedientes"}`}
      alCerrar={alCerrar}
      acciones={
        <Button
          size="sm"
          variant="destructive"
          disabled={borrar.isPending}
          onClick={alBorrar}
        >
          {borrar.isPending ? "Borrando…" : "Borrar de verdad"}
        </Button>
      }
    >
      <p className="mt-1 text-[12.5px] text-muted-foreground">
        No hay papelera: se van la boleta, la ficha, los vouchers y el PDF. Si
        lo que pasa es que están en el mes equivocado,{" "}
        <b>moverlos es lo que corresponde</b>, no borrarlos. Queda anotado en la
        bitácora con el nombre de cada uno.
      </p>
    </Marco>
  );
}

/**
 * LA BARRA DEL LOTE.
 *
 * Aparece solo cuando hay algo elegido y se queda pegada abajo: la lista tiene
 * cien filas y la selección se hace bajando, así que el botón tiene que estar
 * donde está la vista, no en la cabecera que ya quedó arriba.
 */
export function BarraDeLote({
  elegidos,
  meses,
  mesActual,
  alLimpiar,
}: {
  elegidos: string[];
  meses: Mes[];
  mesActual: string;
  alLimpiar: () => void;
}) {
  const [abierto, setAbierto] = useState<"mover" | "borrar" | null>(null);
  if (elegidos.length === 0) return null;

  return (
    <>
      <div className="sticky bottom-4 z-40 mx-auto flex w-fit items-center gap-3 rounded-full border bg-slate-900 px-4 py-2 text-white shadow-lg dark:bg-slate-100 dark:text-slate-900">
        <span className="font-medium text-[12.5px] tabular-nums">
          {elegidos.length} {elegidos.length === 1 ? "elegido" : "elegidos"}
        </span>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setAbierto("mover")}
        >
          Mover a otro mes
        </Button>
        <Button
          size="sm"
          variant="destructive"
          onClick={() => setAbierto("borrar")}
        >
          Borrar
        </Button>
        <button
          type="button"
          className="text-[11.5px] underline opacity-80 hover:opacity-100"
          onClick={alLimpiar}
        >
          quitar la selección
        </button>
      </div>

      {abierto === "mover" ? (
        <MoverExpedientes
          archivoIds={elegidos}
          meses={meses}
          mesActual={mesActual}
          alTerminar={alLimpiar}
          alCerrar={() => setAbierto(null)}
        />
      ) : null}
      {abierto === "borrar" ? (
        <BorrarExpedientes
          archivoIds={elegidos}
          alTerminar={alLimpiar}
          alCerrar={() => setAbierto(null)}
        />
      ) : null}
    </>
  );
}
